import React from 'react';
import { Button } from '@mui/material';
import { Replay as ReplayIcon } from '@mui/icons-material';
import { useMsal, useAccount } from '@azure/msal-react';
import { useAppSelector, useAppDispatch } from '../../../../app/store';
import { useUpdateWriteoffMutation } from '../../../../services/WriteoffService';
import { clearBackupAction as clearBackup } from '../../../../services/WriteoffService/slice';
import { useNavigate } from 'react-router-dom';

interface ResubmitButtonProps {
  onError?: (message: string) => void;
}

export const ResubmitButton: React.FC<ResubmitButtonProps> = ({ onError }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentWriteoff, isEdited } = useAppSelector(state => state.writeoff);
  const [updateWriteoffMutation, { isLoading }] = useUpdateWriteoffMutation();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const isVisible = currentWriteoff?.status?.toUpperCase() === 'REJECTED';

  if (!isVisible) return null;

  const handleResubmit = async () => {
    if (!currentWriteoff) return;

    const modifiedBy = account?.name || account?.username || 'System';
    
    // Reset all approvals when resubmitting
    const resetApprovals = (currentWriteoff.approvals || []).map(approval => ({
      ...approval,
      isApproved: false,
      isRejected: false,
      approvedDate: undefined,
      rejectedDate: undefined,
      approverEmail: undefined,
      approverName: undefined,
      rejectionReason: undefined,
      comments: undefined
    }));

    const writeoffForResubmit = {
      ...currentWriteoff,
      status: 'SUBMITTED',
      modifiedBy,
      approvals: resetApprovals
    };

    try {
      await updateWriteoffMutation({ writeoffView: writeoffForResubmit }).unwrap();
      dispatch(clearBackup());
      navigate(-1);
    } catch (err) {
      onError?.('Failed to resubmit writeoff. Please try again.');
    }
  };

  return (
    <Button
      variant="contained"
      startIcon={<ReplayIcon />}
      onClick={handleResubmit}
      disabled={isLoading || isEdited}
      sx={{
        backgroundColor: '#d97706',
        '&:hover': { backgroundColor: '#b45309' },
        fontWeight: 600
      }}
    >
      {isLoading ? 'Resubmitting...' : 'Resubmit'}
    </Button>
  );
};

export default ResubmitButton;
